import {
  BoxGeometry,
  ConeGeometry,
  CylinderGeometry,
  Group,
  IcosahedronGeometry,
  Mesh,
  MeshBasicMaterial,
  MeshLambertMaterial,
  type BufferGeometry,
  type Material,
} from 'three';

/**
 * EN-02 Helicóptero de la ley (GDD §8.2, issue #84): a low-poly parody of the Bloque de Búsqueda helicopter,
 * with no real insignia, plus a translucent searchlight cone that is not an obstacle (GDD §14.1) and turns
 * amber before the burst. The hitbox is 2 x 1 u, 70-80 % of the model, which spans about 2.7 x 1.3 u. The
 * crash and the weapons of the law are never shown: it cannot be shot down.
 */
export interface LawHelicopter {
  group: Group;
  body: Mesh;
  rotor: Mesh;
  cone: Mesh;
  projectiles: Mesh[];
  setAmber(amber: boolean): void;
  dispose(): void;
}

export const HELICOPTER_COLORS = { body: 0x39424d, stripe: 0xb7ad8a, rotor: 0x15171b, glass: 0x9ec3d6, idle: 0xfff2b0, amber: 0xffb300 } as const;
const PROJECTILE_POOL = 9;

export function buildLawHelicopter(coneLength: number): LawHelicopter {
  const geometries: BufferGeometry[] = [];
  const materials: Material[] = [];
  const paint = (color: number) => {
    const material = new MeshLambertMaterial({ color, flatShading: true });
    materials.push(material);
    return material;
  };
  const own = <T extends BufferGeometry>(geometry: T): T => {
    geometries.push(geometry);
    return geometry;
  };

  const group = new Group();
  // Cabin 1.8 x 1.0, tail boom to the right, skids below: about 2.7 x 1.3 u.
  const body = new Mesh(own(new IcosahedronGeometry(0.5, 0)), paint(HELICOPTER_COLORS.body));
  body.name = 'enemy-en02-body';
  body.scale.set(1.7, 1.0, 1.0);
  const glass = new Mesh(own(new IcosahedronGeometry(0.3, 0)), paint(HELICOPTER_COLORS.glass));
  glass.position.set(-0.55, 0.1, 0.25);
  const boom = new Mesh(own(new BoxGeometry(1.1, 0.2, 0.2)), paint(HELICOPTER_COLORS.body));
  boom.position.set(1.35, 0.15, 0);
  const fin = new Mesh(own(new BoxGeometry(0.2, 0.5, 0.05)), paint(HELICOPTER_COLORS.stripe));
  fin.position.set(1.85, 0.35, 0);
  const skid = new Mesh(own(new BoxGeometry(1.7, 0.06, 0.06)), paint(HELICOPTER_COLORS.rotor));
  skid.position.set(0, -0.62, 0);
  const rotor = new Mesh(own(new BoxGeometry(3, 0.04, 0.12)), paint(HELICOPTER_COLORS.rotor));
  rotor.position.set(0, 0.62, 0);
  const mast = new Mesh(own(new CylinderGeometry(0.05, 0.05, 0.2, 4)), paint(HELICOPTER_COLORS.rotor));
  mast.position.set(0, 0.5, 0);
  group.add(body, glass, boom, fin, skid, rotor, mast);

  // The cone hangs from the helicopter and points down; the scene rotates it to the angle of the sweep.
  const coneMaterial = new MeshBasicMaterial({ color: HELICOPTER_COLORS.idle, transparent: true, opacity: 0.18, depthWrite: false });
  materials.push(coneMaterial);
  const cone = new Mesh(own(new ConeGeometry(coneLength * Math.tan((15 * Math.PI) / 180), coneLength, 12, 1, true)), coneMaterial);
  cone.name = 'enemy-en02-cone';
  // The cone's apex is at its top; moved so that the pivot (the group origin) is the apex.
  cone.geometry.translate(0, -coneLength / 2, 0);

  const bulletMaterial = paint(HELICOPTER_COLORS.amber);
  const projectiles: Mesh[] = [];
  for (let i = 0; i < PROJECTILE_POOL; i++) {
    const bullet = new Mesh(own(new IcosahedronGeometry(0.12, 0)), bulletMaterial);
    bullet.name = 'enemy-en02-projectile';
    bullet.visible = false;
    projectiles.push(bullet);
  }

  return {
    group,
    body,
    rotor,
    cone,
    projectiles,
    setAmber(amber) {
      coneMaterial.color.setHex(amber ? HELICOPTER_COLORS.amber : HELICOPTER_COLORS.idle);
      coneMaterial.opacity = amber ? 0.32 : 0.18;
    },
    dispose() {
      for (const geometry of geometries) geometry.dispose();
      for (const material of materials) material.dispose();
    },
  };
}
